import React, { useEffect, useState } from 'react';
import {
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  message,
  Popconfirm,
  Switch,
  Tabs,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  BankOutlined,
  CreditCardOutlined,
  WechatOutlined,
  DollarOutlined,
  MobileOutlined,
} from '@ant-design/icons';
import { get } from '../../utils/api';
import '../../styles/third-party.css';

const { Option } = Select;

const METHOD_LABELS = {
  bank_transfer: '银行卡转账',
  alipay: '支付宝',
  wechat: '微信',
  usdt: 'USDT',
  'm-pesa': 'M-Pesa',
  mobile_money: 'Mobile Money',
  united_bank: 'United bank',
};

const METHOD_ICON_COLOR = {
  bank_transfer: '#1677ff',
  alipay: '#1677ff',
  wechat: '#07c160',
  usdt: '#f7931a',
  'm-pesa': '#00a651',
  mobile_money: '#6b54d3',
  united_bank: '#1677ff',
};

const TAB_ITEMS = [
  { key: 'all', label: '全部' },
  // { key: 'bank_transfer', label: '银行卡' },
  // { key: 'alipay', label: '支付宝' },
  // { key: 'wechat', label: '微信' },
  { key: 'usdt', label: 'USDT' },
  { key: 'm-pesa', label: 'M-Pesa' },
  { key: 'mobile_money', label: 'Mobile Money' },
  { key: 'united_bank', label: 'United bank' },
];

const PaymentAccounts = () => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [form] = Form.useForm();
  const [activeTab, setActiveTab] = useState('all');
  const [paymentMethod, setPaymentMethod] = useState('');

  useEffect(() => {
    fetchAccounts();
  }, [activeTab]);

  const fetchAccounts = async () => {
    setLoading(true);
    try {
      const params = activeTab !== 'all' ? { paymentMethod: activeTab } : {};
      const res = await get('/admin/payment-accounts', params);
      if (res.code === 200) {
        setAccounts(res.data?.list || res.data?.records || res.data || []);
      }
    } catch (error) {
      message.error('获取收款账户列表失败');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const convertToCamelCase = (record) => {
    if (!record) return null;
    return {
      paymentMethod: record.payment_method,
      accountName: record.account_name,
      bankName: record.bank_name,
      bankAccount: record.bank_account,
      accountHolder: record.account_holder,
      alipayAccount: record.alipay_account,
      wechatAccount: record.wechat_account,
      qrCode: record.qr_code,
      usdtAddress: record.usdt_address,
      usdtNetwork: record.usdt_network,
      usdtExchangeRate: record.usdt_exchange_rate,
      mpesaPhone: record.mpesa_phone,
      mpesaName: record.mpesa_name,
      dailyLimit: record.daily_limit,
      sortOrder: record.sort_order,
      status: record.status === 1,
      remark: record.remark
    };
  };

  const handleOpenModal = (record = null) => {
    setEditingAccount(record);
    if (record) {
      const convertedRecord = convertToCamelCase(record);
      if (convertedRecord.paymentMethod === 'usdt' && !convertedRecord.usdtExchangeRate) {
        convertedRecord.usdtExchangeRate = 7.1;
      }
      form.setFieldsValue(convertedRecord);
      setPaymentMethod(convertedRecord.paymentMethod || '');
    } else {
      form.resetFields();
      setPaymentMethod('');
    }
    setModalVisible(true);
  };

  const handleCloseModal = () => {
    setModalVisible(false);
    setEditingAccount(null);
    setPaymentMethod('');
    form.resetFields();
  };

  const handleSubmit = async (values) => {
    try {
      const payload = {
        paymentMethod: values.paymentMethod,
        accountName: values.accountName,
        bankName: values.bankName,
        bankAccount: values.bankAccount,
        accountHolder: values.accountHolder,
        alipayAccount: values.alipayAccount,
        wechatAccount: values.wechatAccount,
        qrCode: values.qrCode,
        usdtAddress: values.usdtAddress,
        usdtNetwork: values.usdtNetwork,
        mpesaPhone: values.mpesaPhone,
        mpesaName: values.mpesaName,
        dailyLimit: values.dailyLimit,
        sortOrder: values.sortOrder,
        status: values.status ? 1 : 0,
        remark: values.remark
      };

      if (values.paymentMethod === 'usdt') {
        payload.usdtExchangeRate = values.usdtExchangeRate || 7.1;
      }

      const url = editingAccount
        ? `/admin/payment-accounts/${editingAccount.id}`
        : '/admin/payment-accounts';

      const res = await get(url, payload, 'POST');

      if (res.code === 200) {
        message.success(editingAccount ? '收款账户更新成功' : '收款账户创建成功');
        handleCloseModal();
        fetchAccounts();
      }
    } catch (error) {
      message.error(error.message || '操作失败');
      console.error(error);
    }
  };

  const handleDelete = async (id) => {
    try {
      const res = await get(`/admin/payment-accounts/${id}/delete`, {}, 'POST');
      if (res.code === 200) {
        message.success('收款账户删除成功');
        fetchAccounts();
      }
    } catch (error) {
      message.error(error.message || '删除失败');
      console.error(error);
    }
  };

  const handleToggleStatus = async (record) => {
    try {
      const newStatus = record.status === 1 ? 0 : 1;
      const res = await get(`/admin/payment-accounts/${record.id}/status`, { status: newStatus }, 'POST');
      if (res.code === 200) {
        message.success('状态更新成功');
        fetchAccounts();
      }
    } catch (error) {
      message.error(error.message || '操作失败');
      console.error(error);
    }
  };

  const renderPaymentIcon = (method) => {
    const color = METHOD_ICON_COLOR[method];
    const icons = {
      bank_transfer: <BankOutlined style={{ color }} />,
      alipay: <CreditCardOutlined style={{ color }} />,
      wechat: <WechatOutlined style={{ color }} />,
      usdt: <DollarOutlined style={{ color }} />,
      'm-pesa': <MobileOutlined style={{ color }} />,
      mobile_money: <MobileOutlined style={{ color }} />,
      united_bank: <BankOutlined style={{ color }} />,
    };
    return icons[method] || null;
  };

  const renderPaymentFields = () => {
    if (paymentMethod === 'bank_transfer') {
      return (
        <>
          <Form.Item
            name="bankName"
            label="银行名称"
            rules={[{ required: true, message: '请输入银行名称' }]}
          >
            <Input placeholder="如：招商银行" />
          </Form.Item>
          <Form.Item
            name="bankAccount"
            label="银行账号"
            rules={[{ required: true, message: '请输入银行账号' }]}
          >
            <Input placeholder="银行账号" />
          </Form.Item>
          <Form.Item
            name="accountHolder"
            label="账户持有人"
            rules={[{ required: true, message: '请输入账户持有人' }]}
          >
            <Input placeholder="账户持有人姓名" />
          </Form.Item>
        </>
      );
    }

    if (paymentMethod === 'alipay') {
      return (
        <>
          <Form.Item
            name="alipayAccount"
            label="支付宝账号"
            rules={[{ required: true, message: '请输入支付宝账号' }]}
          >
            <Input placeholder="支付宝账号" />
          </Form.Item>
          <Form.Item
            name="qrCode"
            label="收款二维码URL"
          >
            <Input placeholder="收款二维码图片URL" />
          </Form.Item>
        </>
      );
    }

    if (paymentMethod === 'wechat') {
      return (
        <>
          <Form.Item
            name="wechatAccount"
            label="微信账号"
            rules={[{ required: true, message: '请输入微信账号' }]}
          >
            <Input placeholder="微信号" />
          </Form.Item>
          <Form.Item
            name="qrCode"
            label="收款二维码URL"
          >
            <Input placeholder="收款二维码图片URL" />
          </Form.Item>
        </>
      );
    }

    if (paymentMethod === 'usdt') {
      return (
        <>
          <Form.Item
            name="usdtAddress"
            label="USDT收款地址"
            rules={[{ required: true, message: '请输入USDT收款地址' }]}
          >
            <Input placeholder="USDT钱包地址" />
          </Form.Item>
          <Form.Item
            name="usdtNetwork"
            label="网络类型"
            rules={[{ required: true, message: '请选择网络类型' }]}
          >
            <Select placeholder="选择网络类型">
              <Option value="TRC20">TRC20</Option>
              <Option value="ERC20">ERC20</Option>
              <Option value="BEP20">BEP20</Option>
              <Option value="OMNI">OMNI</Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="usdtExchangeRate"
            label="USDT汇率（兑人民币）"
            tooltip="USDT兑人民币的汇率，默认值为7.1"
            rules={[
              { required: true, message: '请输入USDT汇率' },
              { type: 'number', min: 0.0001, message: '汇率必须大于0' }
            ]}
            initialValue={7.1}
          >
            <InputNumber
              style={{ width: '100%' }}
              min={0.0001}
              precision={4}
              placeholder="请输入USDT兑人民币汇率"
            />
          </Form.Item>
        </>
      );
    }

    if (paymentMethod === 'm-pesa') {
      return (
        <>
          <Form.Item
            name="mpesaPhone"
            label="M-Pesa手机号"
            rules={[{ required: true, message: '请输入M-Pesa手机号' }]}
          >
            <Input placeholder="如：+254712345678" />
          </Form.Item>
          <Form.Item
            name="mpesaName"
            label="M-Pesa账户名"
            rules={[{ required: true, message: '请输入M-Pesa账户名' }]}
          >
            <Input placeholder="账户持有人姓名" />
          </Form.Item>
        </>
      );
    }

    if (paymentMethod === 'mobile_money') {
      return (
        <>
          <Form.Item
            name="mpesaPhone"
            label="Mobile Money手机号"
            rules={[{ required: true, message: '请输入Mobile Money手机号' }]}
          >
            <Input placeholder="如：+254712345678" />
          </Form.Item>
          <Form.Item
            name="mpesaName"
            label="Mobile Money账户名"
            rules={[{ required: true, message: '请输入Mobile Money账户名' }]}
          >
            <Input placeholder="账户持有人姓名" />
          </Form.Item>
        </>
      );
    }

    if (paymentMethod === 'united_bank') {
      return (
        <>
          <Form.Item
            name="bankAccount"
            label="United bank 卡号"
            rules={[{ required: true, message: '请输入United bank卡号' }]}
          >
            <Input placeholder="United bank 卡号" />
          </Form.Item>
          <Form.Item
            name="accountHolder"
            label="United bank 姓名"
            rules={[{ required: true, message: '请输入United bank姓名' }]}
          >
            <Input placeholder="United bank 姓名" />
          </Form.Item>
        </>
      );
    }

    return null;
  };

  const renderAccountInfo = (record) => {
    if (record.payment_method === 'bank_transfer') {
      return (
        <div className="tp-account">
          <div className="tp-account__main">{record.bank_name} · {record.bank_account}</div>
          {record.account_holder && <div className="tp-account__sub">{record.account_holder}</div>}
        </div>
      );
    }
    if (record.payment_method === 'alipay') {
      return <div className="tp-account__main">{record.alipay_account || <span className="tp-empty">-</span>}</div>;
    }
    if (record.payment_method === 'wechat') {
      return <div className="tp-account__main">{record.wechat_account || <span className="tp-empty">-</span>}</div>;
    }
    if (record.payment_method === 'usdt') {
      return (
        <div className="tp-account">
          <div className="tp-account__main" title={record.usdt_address}>{record.usdt_address}</div>
          <div className="tp-account__sub">
            {record.usdt_network && <span className="tp-tag tp-tag--muted">{record.usdt_network}</span>}
            {record.usdt_exchange_rate && (
              <span className="tp-tag tp-tag--pending">汇率 {parseFloat(record.usdt_exchange_rate).toFixed(4)}</span>
            )}
          </div>
        </div>
      );
    }
    if (record.payment_method === 'm-pesa' || record.payment_method === 'mobile_money') {
      return (
        <div className="tp-account">
          <div className="tp-account__main">{record.mpesa_phone}</div>
          {record.mpesa_name && <div className="tp-account__sub">{record.mpesa_name}</div>}
        </div>
      );
    }
    if (record.payment_method === 'united_bank') {
      return (
        <div className="tp-account">
          <div className="tp-account__main">{record.bank_account}</div>
          {record.account_holder && <div className="tp-account__sub">{record.account_holder}</div>}
        </div>
      );
    }
    return <span className="tp-empty">-</span>;
  };

  const columns = [
    {
      title: '支付方式',
      dataIndex: 'payment_method',
      key: 'payment_method',
      width: 150,
      render: (method) => (
        <span className="tp-method">
          <span className="tp-method__icon">{renderPaymentIcon(method)}</span>
          <span className="tp-method__name">{METHOD_LABELS[method] || method}</span>
        </span>
      ),
    },
    {
      title: '账户名称',
      dataIndex: 'account_name',
      key: 'account_name',
      ellipsis: true,
      render: (val) => val ? <span className="tp-ellipsis" title={val}>{val}</span> : <span className="tp-empty">-</span>
    },
    {
      title: '收款信息',
      key: 'account_info',
      render: (_, record) => renderAccountInfo(record),
    },
    {
      title: '每日限额',
      dataIndex: 'daily_limit',
      key: 'daily_limit',
      width: 130,
      render: (limit) => {
        const amount = parseFloat(limit);
        return amount > 0
          ? <span className="tp-num">¥{amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          : <span className="tp-empty">不限</span>;
      },
    },
    {
      title: '排序',
      dataIndex: 'sort_order',
      key: 'sort_order',
      width: 72,
      render: (val) => <span className="tp-num">{val ?? 0}</span>,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 140,
      render: (status, record) => (
        <div className="tp-status-cell">
          <Switch size="small" checked={status === 1} onChange={() => handleToggleStatus(record)} />
          <span className={`tp-tag ${status === 1 ? 'tp-tag--paid' : 'tp-tag--muted'}`}>
            {status === 1 ? '启用' : '停用'}
          </span>
        </div>
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 128,
      render: (_, record) => (
        <div className="tp-actions">
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleOpenModal(record)}>
            编辑
          </Button>
          <Popconfirm
            title="确定删除此收款账户吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="link" size="small" className="tp-link--danger">
              删除
            </Button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <div className="tp-page">
      <div className="tp-card tp-toolbar tp-toolbar--split">
        <Button type="primary" icon={<PlusOutlined />} onClick={() => handleOpenModal()}>
          添加收款账户
        </Button>
        <Tabs activeKey={activeTab} onChange={setActiveTab} items={TAB_ITEMS} />
      </div>

      <div className="tp-card tp-table-card">
        <Table
          columns={columns}
          dataSource={accounts}
          rowKey="id"
          loading={loading}
          pagination={false}
          size="middle"
          scroll={{ x: 960 }}
        />
      </div>

      <Modal
        className="tp-modal"
        title={editingAccount ? '编辑收款账户' : '添加收款账户'}
        open={modalVisible}
        onCancel={handleCloseModal}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="paymentMethod"
            label="支付方式"
            rules={[{ required: true, message: '请选择支付方式' }]}
          >
            <Select
              placeholder="选择支付方式"
              disabled={!!editingAccount}
              onChange={(value) => {
                setPaymentMethod(value);
                if (value === 'usdt') {
                  const currentRate = form.getFieldValue('usdtExchangeRate');
                  if (!currentRate) {
                    form.setFieldsValue({ usdtExchangeRate: 7.1 });
                  }
                }
              }}
            >
              {/* <Option value="bank_transfer">银行卡转账</Option> */}
              {/* <Option value="alipay">支付宝</Option> */}
              {/* <Option value="wechat">微信</Option> */}
              <Option value="usdt">USDT</Option>
              <Option value="m-pesa">M-Pesa</Option>
              <Option value="mobile_money">Mobile Money</Option>
              <Option value="united_bank">United bank</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="accountName"
            label="账户名称"
            rules={[{ required: true, message: '请输入账户名称' }]}
          >
            <Input placeholder="如：招商银行收款账户001" />
          </Form.Item>

          {renderPaymentFields()}

          <Form.Item
            name="dailyLimit"
            label="每日限额"
            tooltip="0表示不限制"
          >
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              precision={2}
              placeholder="每日收款限额"
            />
          </Form.Item>

          <Form.Item
            name="sortOrder"
            label="排序"
          >
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              placeholder="排序权重，数字越小越靠前"
            />
          </Form.Item>

          <Form.Item
            name="status"
            label="状态"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          <Form.Item
            name="remark"
            label="备注"
          >
            <Input.TextArea rows={3} placeholder="备注信息" />
          </Form.Item>

          <Form.Item className="tp-modal-actions">
            <Space>
              <Button onClick={handleCloseModal}>取消</Button>
              <Button type="primary" htmlType="submit">
                {editingAccount ? '更新' : '创建'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default PaymentAccounts;
